import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Sparkles, Users2, BedDouble, ClipboardList, BarChart3, ChevronDown, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/**
 * MgmtDashboard — read-only "Fleet Manager" view for a management_company
 * user. Five tabs: Treatments / Crew / Cabins / Guests / Treatment Stats.
 *
 * PII: guest names come pre-masked from the backend when the Developer
 * has flipped `mgmt_mask_names`; the frontend never has to mask
 * anything itself, and never receives real names when masking is on.
 */
export default function MgmtDashboard() {
  const navigate = useNavigate();
  const { logout, admin } = useAuth();
  const [tab, setTab] = useState("treatments");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = {};

  const load = async (which) => {
    setLoading(true);
    setError(null);
    try {
      if (which === "treatments" && !data.treatments) {
        const r = await axios.get(`${API}/mgmt/treatments`, { headers, withCredentials: true });
        setData((d) => ({ ...d, treatments: r.data }));
      } else if (which === "crew" && !data.crew) {
        const r = await axios.get(`${API}/mgmt/crew`, { headers, withCredentials: true });
        setData((d) => ({ ...d, crew: r.data }));
      } else if (which === "cabins" && !data.cabins) {
        const r = await axios.get(`${API}/mgmt/cabins`, { headers, withCredentials: true });
        setData((d) => ({ ...d, cabins: r.data }));
      } else if (which === "guests" && !data.charters) {
        const r = await axios.get(`${API}/mgmt/charters`, { headers, withCredentials: true });
        setData((d) => ({ ...d, charters: r.data }));
      } else if (which === "stats" && !data.stats) {
        const r = await axios.get(`${API}/mgmt/statistics/treatments`, { headers, withCredentials: true });
        setData((d) => ({ ...d, stats: r.data }));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const tabs = [
    { key: "treatments", label: "Treatments", Icon: Sparkles },
    { key: "crew", label: "Crew", Icon: Users2 },
    { key: "cabins", label: "Cabins", Icon: BedDouble },
    { key: "guests", label: "Guests", Icon: ClipboardList },
    { key: "stats", label: "Treatment Stats", Icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="mgmt-dashboard">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur z-20">
        <div>
          <div className="overline text-primary/60 text-[10px]">Management Company · Read-only</div>
          <h1 className="font-display text-2xl tracking-tight">Fleet Dashboard</h1>
        </div>
        <Button variant="outline" size="sm" onClick={doLogout} data-testid="mgmt-logout">
          <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
        </Button>
      </header>

      <nav className="border-b border-border px-4 flex overflow-x-auto">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`mgmt-tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      <main className="px-6 py-8 max-w-6xl mx-auto">
        {error && <div className="mb-6 border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm">{error}</div>}
        {loading && <div className="text-muted-foreground text-sm">Loading…</div>}

        {!loading && tab === "treatments" && <TreatmentsTab items={data.treatments || []} />}
        {!loading && tab === "crew" && <CrewTab items={data.crew || []} />}
        {!loading && tab === "cabins" && <CabinsTab items={data.cabins || []} />}
        {!loading && tab === "guests" && <GuestsTab charters={data.charters || []} />}
        {!loading && tab === "stats" && <StatsTab data={data.stats} />}
      </main>
    </div>
  );
}

function TreatmentsTab({ items }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">No treatments defined.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mgmt-treatments">
      {items.map((t) => (
        <div key={t.id} className="border border-border p-4" data-testid={`mgmt-treatment-${t.id}`}>
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-display text-lg">{t.name}</div>
            <div className="font-mono text-sm text-accent">€{Number(t.price || 0).toFixed(0)}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{t.duration_min} min · {t.category_name || "—"}</div>
          {t.description && <p className="text-sm text-foreground/80 mt-2 line-clamp-3">{t.description}</p>}
        </div>
      ))}
    </div>
  );
}

function CrewTab({ items }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">No crew members.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="mgmt-crew">
      {items.map((c) => (
        <div key={c.id} className="border border-border p-4 flex items-center gap-3" data-testid={`mgmt-crew-${c.id}`}>
          <div>
            <div className="font-display text-base">{c.name}</div>
            <div className="text-xs text-muted-foreground">{c.title || c.rank || "Crew"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CabinsTab({ items }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">No cabins configured.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mgmt-cabins">
      {items.map((c) => (
        <div key={c.id} className="border border-border p-4" data-testid={`mgmt-cabin-${c.id}`}>
          <div className="font-display text-lg">{c.name}</div>
          <div className="text-xs text-muted-foreground mt-1">Capacity: {c.capacity || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function GuestsTab({ charters }) {
  const [expanded, setExpanded] = useState(null);
  if (!charters.length) return <p className="text-sm text-muted-foreground">No charters on file.</p>;
  return (
    <div className="space-y-3" data-testid="mgmt-charters">
      {charters.map((c) => {
        const open = expanded === c.id;
        return (
          <div key={c.id} className="border border-border" data-testid={`mgmt-charter-${c.id}`}>
            <button
              onClick={() => setExpanded(open ? null : c.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
            >
              <div className="text-left">
                <div className="font-display text-base">{c.name || "Charter"}</div>
                <div className="text-xs text-muted-foreground">
                  {(c.arrival || "").slice(0, 10)} → {(c.departure || "").slice(0, 10)} · {(c.guests || []).length} guests
                </div>
              </div>
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {open && (
              <div className="px-4 pb-4 pt-1 border-t border-border">
                {(c.guests || []).length === 0 && <p className="text-sm text-muted-foreground">No guests recorded.</p>}
                {(c.guests || []).length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="text-left py-2">Name / Alias</th>
                        <th className="text-left py-2">Cabin</th>
                        <th className="text-left py-2">Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(c.guests || []).map((g, idx) => (
                        <tr key={g.guest_id || g.id || idx} className="border-t border-border/50">
                          <td className="py-2">{g.name || "—"}</td>
                          <td className="py-2">{g.cabin_name || "—"}</td>
                          <td className="py-2">{g.minor ? "Minor" : (g.age ?? "—")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatsTab({ data }) {
  const [expand, setExpand] = useState(null);
  if (!data) return <p className="text-sm text-muted-foreground">Loading statistics…</p>;
  const rows = data.rows || [];
  const t = data.totals || {};
  return (
    <div data-testid="mgmt-stats">
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border p-3">
          <div className="overline text-primary/60 text-[10px]">Bookings served</div>
          <div className="font-display text-2xl">{t.count || 0}</div>
        </div>
        <div className="border border-border p-3">
          <div className="overline text-primary/60 text-[10px]">Revenue</div>
          <div className="font-display text-2xl">€{Number(t.revenue || 0).toFixed(0)}</div>
        </div>
        <div className="border border-border p-3">
          <div className="overline text-primary/60 text-[10px]">Charters</div>
          <div className="font-display text-2xl">{t.charters || 0}</div>
        </div>
      </div>

      {rows.length === 0 && <p className="text-sm text-muted-foreground">No completed bookings yet.</p>}
      {rows.length > 0 && (
        <div className="border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-2">Treatment</th>
                <th className="text-right p-2">Bookings</th>
                <th className="text-right p-2">Revenue</th>
                <th className="text-right p-2">Charters</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.treatment_id}>
                  <tr
                    onClick={() => setExpand(expand === r.treatment_id ? null : r.treatment_id)}
                    className="border-t border-border cursor-pointer hover:bg-muted/20"
                    data-testid={`mgmt-stat-row-${r.treatment_id}`}
                  >
                    <td className="p-2">{r.treatment_name}</td>
                    <td className="p-2 text-right font-mono">{r.count}</td>
                    <td className="p-2 text-right font-mono">€{Number(r.revenue).toFixed(0)}</td>
                    <td className="p-2 text-right font-mono">{r.per_charter.length}</td>
                    <td className="p-2 text-right">{expand === r.treatment_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                  </tr>
                  {expand === r.treatment_id && (
                    <tr className="bg-muted/10">
                      <td colSpan={5} className="p-3">
                        <div className="overline text-primary/60 text-[10px] mb-2">Per-charter breakdown</div>
                        <ul className="text-xs space-y-1">
                          {r.per_charter.map((pc) => (
                            <li key={pc.charter_id} className="flex items-center justify-between gap-3 border-b border-border/50 py-1">
                              <span className="truncate">{pc.charter_name || pc.charter_id.slice(-6)} · {(pc.arrival || "").slice(0, 10)}</span>
                              <span className="font-mono text-muted-foreground">{pc.count} bookings · €{Number(pc.revenue).toFixed(0)}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
